import { OnMessageHandler, ErrorHandler, OpenHandler, CloseHandler, MessageOption, WsOptions, SubscribeData } from '../types/socket';
import { buildWebSocketUrl, createWebSocket, parseeSubscribeType, parseSymbols } from './index';


export default class SocketClient {
    // WebSocket connection related properties
    protected socket: WebSocket | null = null;
    protected wssURL: string;
    protected wsPath: string;
    protected token: string;
    protected isRunning: boolean = false;
    protected reconnectAttempts: number = 0;
    protected pingInterval: NodeJS.Timeout | null = null;
    protected reconnectTimeout: NodeJS.Timeout | null = null;
    // WebSocket constants
    protected PING_INTERVAL: number; // ping interval
    protected RECONNECT_INTERVAL: number; // reconnection interval
    protected MAX_RECONNECT_ATTEMPTS: number; // maximum reconnection attempts

    // Callback functions
    protected messageHandlers: OnMessageHandler[] = [];
    protected errorHandlers: ErrorHandler[] = [];
    protected openHandlers: OpenHandler[] = [];
    protected closeHandlers: CloseHandler[] = [];

    // Subscription information storage
    protected lastSubscription: SubscribeData | null = null;

    constructor(token: string, options: WsOptions) {
        this.wssURL = options.wssURL ?? 'wss://api.itick.org';
        this.wsPath = options.wsPath;
        this.token = token;
        this.RECONNECT_INTERVAL = options.reconnectInterval ?? 5000;
        this.MAX_RECONNECT_ATTEMPTS = options.maxReconnectTimes ?? 0;
        this.PING_INTERVAL = options.pingInterval ?? 30000;
        this.lastSubscription = options.subscribeData ?? null;
        this._connectWebSocket();
    }
    /**
     *  WebSocket Message Handler
     */
    onSocketMessage(handler: OnMessageHandler): void {
        this.messageHandlers.push(handler);
    }

    /**
     *  WebSocket Error Handler
     */
    onSocketError(handler: ErrorHandler): void {
        this.errorHandlers.push(handler);
    }

    /**
     * WebSocket Connection Open Handler
     */
    onSocketOpen(handler: OpenHandler): void {
        this.openHandlers.push(handler);
    }

    /**
     * WebSocket Connection Close Handler
     */
    onSocketClose(handler: CloseHandler): void {
        this.closeHandlers.push(handler);
    }

    /**
     * Internal WebSocket Connection Method
     */
    private async _connectWebSocket(): Promise<void> {
        const url = buildWebSocketUrl(this.wssURL, this.wsPath || '');
        try {
            this.isRunning = true;
            this.socket = await createWebSocket(url, this.token);

            this.socket.onopen = () => {
                this.reconnectAttempts = 0;
                this._startPing();
                this.openHandlers.forEach(handler => handler());
                // Subscribe to data after successful connection
                this._resubscribeLast();
            };

            this.socket.onmessage = (event: MessageEvent) => {
                const messageStr = typeof event.data === 'string' ? event.data : event.data.toString();
                this.messageHandlers.forEach(handler => handler(JSON.parse(messageStr)));
            };

            this.socket.onclose = (event: CloseEvent) => {
                this._stopPing();
                this.closeHandlers.forEach(handler => handler(event));
                if (this.isRunning) {
                    this._scheduleReconnect();
                }
            };

            this.socket.onerror = (event: Event) => {
                this.errorHandlers.forEach(handler => handler(event));
            };
        } catch (error) {
            this.errorHandlers.forEach(handler => handler(error as Error));
            if (this.isRunning) {
                this._scheduleReconnect();
            }
        }
    }
    /**
     * Start heartbeat ping
    */
    protected _startPing(): void {
        this._stopPing();
        this.pingInterval = setInterval(() => {
            if (this.socket && this.socket.readyState === WebSocket.OPEN) {
                this.socket.send(JSON.stringify({ ac: "ping", params: Date.now() }));
            }
        }, this.PING_INTERVAL);
    }

    /**
     * Stop heartbeat ping
     */
    protected _stopPing(): void {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }
    }

    /**
     * Schedule reconnection
     */
    protected _scheduleReconnect(): void {
        // When MAX_RECONNECT_ATTEMPTS is 0, it means unlimited reconnection
        if (this.MAX_RECONNECT_ATTEMPTS > 0 && this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
            this.errorHandlers.forEach(handler => handler(new Error('Maximum reconnect attempts reached')));
            return;
        }

        this.reconnectAttempts++;
        this.reconnectTimeout = setTimeout(() => {
            if (this.isRunning) {
                this._connectWebSocket().catch((error) => { });
            }
        }, this.RECONNECT_INTERVAL);
    }

    /**
     * WebSocket Subscription Method
     * @description
     * message object must contain ac, types and params fields
     * - ac: operation type, must be "subscribe" or "unsubscribe"
     * - types: subscription types, can be comma-separated string or string array, supports "quote", "depth", "tick", "kline@1m" etc. kline@1m can also be written as kline@1, SDK will automatically convert to correct format
     * - params: subscription parameters, can be comma-separated string or string array, format is "code$region" (e.g., "AAPL$US") SDK will automatically handle formatting
     * - String array format is recommended for clearer code and fewer errors
     * @example
     * Example 1: Subscribe to AAPL and TSA real-time quotes/order book/trades/1-minute K-line data
     * ```json
     * {
     *   "ac": "subscribe",
     *   "types": "quote,depth,tick,kline@1",
     *   "params": "AAPL$US,TSA$US"
     *  }
     * ```
     * Example 2: Subscribe to AAPL and TSA real-time quotes/order book/trades/1-minute K-line data
     * ```json
     * {
     *   "ac": "subscribe",
     *   "types": ["quote","depth","tick","kline@1m"],
     *   "params": ["AAPL$US","TSA$US"]
     *  }
     * ```
     * 
     * Both methods can correctly subscribe, SDK will automatically handle type conversion and parameter formatting
     * 
     */
    subscribeSocket(data: MessageOption): void {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            throw new Error('WebSocket not connected');
        }
        try {
            let { ac, types, codes } = data;
            const type = parseeSubscribeType(types);
            const symbol = parseSymbols(codes);
            this.socket.send(JSON.stringify({ ac, types: type, params: symbol }));
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * Restore subscription after reconnection
     */
    protected _resubscribeLast(): void {
        if (!this.lastSubscription) return;
        try {
            let { types, codes } = this.lastSubscription;
            const type = parseeSubscribeType(types);
            const symbol = parseSymbols(codes);
            this.socket?.send(JSON.stringify({ ac: 'subscribe', types: type, params: symbol }));
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * Close WebSocket
     */
    closeWebSocket(): void {
        this.isRunning = false;
        this._stopPing();
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
        }
        if (this.socket) {
            this.socket.close();
        }
        // Clear last subscription information when closing
        this.lastSubscription = null;
    }

    /**
     * Check if WebSocket is connected
     */
    checkSocketConnected(): boolean {
        if (!this.socket) return false;
        return this.socket.readyState === WebSocket.OPEN;
    }

    disconnectSocket() {
        this.closeWebSocket();
    }
}